import {
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
  type RefObject,
} from 'react'
import { useSelector } from 'react-redux'
import {
  ArrowUpIcon,
  Button,
  Checkbox,
  Grid,
  P,
  PencilIcon,
  PhoneMissedSolidIcon,
  PhoneSolidIcon,
  Wrapper,
} from '@farewill/ui'
import { screenMin } from '@farewill/ui/helpers/responsive'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { atLondonTime, dayjsLondon, dayjsUtc, londonToday } from 'lib/dayjs'
import { Formik } from 'formik'
import { getUserIdFromToken } from 'lib/authentication/token'
import styled from 'styled-components'

import {
  getFullNameWithPreferredName,
  dateIsToday,
  formatToHuman,
  truncateText,
  formatProductName,
  getTimeDescriptionFromTimestamp,
} from 'utils/helpers'

import { LEAD_EVENT_TYPES, LEAD_STATUS, PRODUCTS } from 'utils/enums'
import { leadScoreLevelsFor } from 'utils/lead-score-levels'
import { useFetchArrayResource, RESOURCES } from 'api'

import StyledGridRow from 'components/list/grid-row'
import StyledLink from 'components/list/link'
import NoResults from 'components/list/no-results'
import ErrorResults from 'components/list/error-results'
import StyledLoadingRow from 'components/list/loading-row'
import { SortableColumn } from 'components/list/sortable-column'
import DateBadge from 'components/date-badge'
import PageButtons from 'components/list/page-buttons'
import Tooltip from 'components/tooltip'

import FilterPanel, { Filters } from './filter-panel'
import ConversionScoreIcon from 'components/conversion-score-icon'
import OwnerAssignment from './owner-assignment'
import BulkAssignDropdown from './bulk-assign-dropdown'
import { LeadEventAttributes } from 'lib/models/lead-event'
import { Lead, LeadAttributes } from 'lib/models/lead'
import useLocalStorage from 'hooks/useLocalStorage'
import useSearchParamManager, {
  FilterValues,
  PageValues,
  SortValue,
} from 'hooks/useSearchParamManager'

const LastActivityIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'added':
      return <ArrowUpIcon size="S" />
    case 'call_made_connected':
      return <PhoneSolidIcon size="S" />
    case 'call_made_no_answer':
      return <PhoneMissedSolidIcon size="S" />
    case 'comment_added':
      return <PencilIcon size="S" />
    default:
      return null
  }
}

const StyledGridColumn = styled.li`
  font-size: ${FONT.SIZE.S};
  display: inline-flex;
`

const StyledCheckboxColumn = styled(StyledGridColumn)`
  justify-content: center;
`

const LeadDataRow = styled(StyledGridRow)`
  &:hover {
    background-color: ${COLOR.BACKGROUND.FOG};
  }
`

const LeadRowLinkContents = styled(StyledLink)`
  display: contents;
  color: inherit;
`
const StyledActivityWrapper = styled(Wrapper)`
  display: flex;
`

const StyledIconWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  color: ${COLOR.GREY.MEDIUM};
`

const LastActivityTime = styled.span`
  margin-left: ${GTR.XS};
`

const StyledTooltip = styled(Tooltip)`
  padding: ${GTR.XXS};
  background-color: ${COLOR.GREY.DARK};
`

const StyledTooltipP = styled(P)`
  margin: 0;
  color: ${COLOR.WHITE};
  font-size: ${FONT.SIZE.S};
`

const StyledSpan = styled.span`
  font-weight: ${FONT.WEIGHT.BOLD};
`

const StyledP = styled(P)`
  text-align: right;
`

const StyledLeadsListHeader = styled(Wrapper)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`

const StyledCallCountSummary = styled(Wrapper)`
  display: flex;
  flex-direction: row;
  gap: ${GTR.M};

  ${screenMin.s`
    justify-content: flex-end;
  `}
`

const StyledBulkAssignWrapper = styled(Wrapper)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledLeadsSelectedSummaryWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
`

// Each row is its own grid, so columns must ignore their content's width
// (minmax(0, …)) to line up with the header and the other rows
const GRID_TEMPLATE_COLUMNS_BASE = [1.1, 1, 0.75, 0.75, 0.5, 1, 0.5, 1]
  .map((fraction) => `minmax(0, ${fraction}fr)`)
  .join(' ')

const SORT_CONFIG: Record<string, SortValue<LeadAttributes>> = {
  'Call scheduled': {
    sortColumn: 'scheduledNextCallAt',
    reverseNulls: true,
  },
  'Last activity': {
    sortColumn: 'lastLeadEventHappenedAt',
    reverseNulls: false,
  },
  Calls: {
    sortColumn: 'callAttemptCount',
    reverseNulls: false,
  },
  Score: {
    sortColumn: 'conversionScore',
    reverseNulls: false,
  },
  Stage: {
    sortColumn: 'stageNumber',
    reverseNulls: false,
  },
}

const formatLastActivityTime = (date: string) => {
  if (dateIsToday({ date })) return dayjsLondon(date).format('HH:mm')
  const timeAgo = dayjsUtc(dayjsLondon(date).toDate()).from(
    dayjsUtc(atLondonTime(londonToday(), { hour: 0 }).toDate())
  )
  return timeAgo === 'a day ago' ? '1 day ago' : timeAgo
}

/**
 * Funeral plan leads with these statuses were moved onto the seven levels.
 * Converted and lost leads kept their old 1 to 3 score, so the new text would
 * mislabel them (ADR 0003, decision 13).
 */
const STATUSES_ON_SEVEN_LEVELS = [
  LEAD_STATUS.OPEN,
  LEAD_STATUS.QUALIFIED,
  'blocked',
]

/** What a funeral plan lead's score means, when it's on the seven levels */
const describeScore = ({
  product,
  status,
  conversionScore,
}: LeadAttributes): string | undefined => {
  if (
    product !== PRODUCTS.FUNERAL_PLAN ||
    !STATUSES_ON_SEVEN_LEVELS.includes(status)
  ) {
    return undefined
  }

  const level = leadScoreLevelsFor(product).find(
    ({ level }) => level === conversionScore
  )
  return level && `${level.level} - ${level.description}`
}

const getLastLeadEventIconType = (lastLeadEvent: LeadEventAttributes) => {
  return lastLeadEvent.type === LEAD_EVENT_TYPES.CALL_MADE
    ? `${lastLeadEvent.type}_${lastLeadEvent.metadata.disposition}`
    : lastLeadEvent.type
}

export const LeadGrid = ({
  leads = [],
  sort,
  setSort,
  isLoading,
  isError,
  loggedInAdminUserId,
  setEnabled,
  selectedLeadIds,
  onToggleLead,
  onToggleSelectAll,
  selectAllCheckboxRef,
  allPageSelected,
  showBulkAssign,
}: {
  leads: Lead[]
  sort: SortValue<LeadAttributes>
  setSort: (sort: SortValue<LeadAttributes>) => void
  isLoading: boolean
  isError: boolean
  loggedInAdminUserId: number
  setEnabled: Dispatch<SetStateAction<boolean>>
  selectedLeadIds: number[]
  onToggleLead: (leadId: number) => void
  onToggleSelectAll: () => void
  selectAllCheckboxRef: RefObject<HTMLInputElement>
  allPageSelected: boolean
  showBulkAssign: boolean
}) => {
  const gridTemplateColumns = showBulkAssign
    ? `${GRID_TEMPLATE_COLUMNS_BASE} 30px`
    : GRID_TEMPLATE_COLUMNS_BASE
  if (isError) {
    return <ErrorResults type="leads" />
  }

  return (
    <>
      <StyledGridRow header gridTemplateColumns={gridTemplateColumns}>
        <SortableColumn
          columnTitle="Call scheduled"
          columnSortValues={SORT_CONFIG['Call scheduled']}
          sortedValues={sort}
          setSort={setSort}
        />
        <StyledGridColumn>Customer</StyledGridColumn>
        <StyledGridColumn>Product</StyledGridColumn>
        <SortableColumn
          columnTitle="Stage"
          columnSortValues={SORT_CONFIG['Stage']}
          sortedValues={sort}
          setSort={setSort}
        />
        <SortableColumn
          columnTitle="Calls"
          columnSortValues={SORT_CONFIG['Calls']}
          sortedValues={sort}
          setSort={setSort}
          tooltip="How many calls we’ve logged on this lead, answered or not."
        />
        <SortableColumn
          columnTitle="Last activity"
          columnSortValues={SORT_CONFIG['Last activity']}
          sortedValues={sort}
          setSort={setSort}
        />
        <SortableColumn
          columnTitle="Score"
          columnSortValues={SORT_CONFIG['Score']}
          sortedValues={sort}
          setSort={setSort}
        />
        <StyledGridColumn>Owner</StyledGridColumn>
        {showBulkAssign && (
          <StyledCheckboxColumn>
            <Checkbox
              ref={selectAllCheckboxRef}
              checked={allPageSelected && leads.length > 0}
              disabled={leads.length === 0 || isLoading}
              onChange={onToggleSelectAll}
              aria-label="Select all leads on this page"
            />
          </StyledCheckboxColumn>
        )}
      </StyledGridRow>

      {isLoading && <StyledLoadingRow>Loading...</StyledLoadingRow>}
      {!isLoading && leads.length === 0 && (
        <StyledLoadingRow>
          <NoResults type="leads" />
        </StyledLoadingRow>
      )}

      {leads.map((row) => (
        <LeadRow
          key={row.id}
          row={row}
          loggedInAdminUserId={loggedInAdminUserId}
          setEnabled={setEnabled}
          isSelected={selectedLeadIds.includes(row.id)}
          onToggleLead={onToggleLead}
          showBulkAssign={showBulkAssign}
        />
      ))}
    </>
  )
}

const LeadRow = ({
  row,
  loggedInAdminUserId,
  setEnabled,
  isSelected,
  onToggleLead,
  showBulkAssign,
}: {
  row: Lead
  loggedInAdminUserId: number
  setEnabled: Dispatch<SetStateAction<boolean>>
  isSelected: boolean
  onToggleLead: (leadId: number) => void
  showBulkAssign: boolean
}) => {
  let lastLeadEventIconType

  const {
    lastLeadEvent,
    conversionScore,
    callAttemptCount,
    stageName,
    scheduledNextCallAtTimeSet,
    scheduledNextCallAt,
  } = row.attributes
  const contactName = getFullNameWithPreferredName(row.attributes.contact)
  const productName = formatProductName(row.attributes.product)

  if (lastLeadEvent) {
    lastLeadEventIconType = getLastLeadEventIconType(lastLeadEvent)
  }

  return (
    <LeadDataRow
      gridTemplateColumns={
        showBulkAssign
          ? `${GRID_TEMPLATE_COLUMNS_BASE} 30px`
          : GRID_TEMPLATE_COLUMNS_BASE
      }
    >
      <LeadRowLinkContents to={`/leads/${row.id}/history`}>
        <StyledGridColumn>
          <StyledTooltip
            enabled={scheduledNextCallAtTimeSet}
            underlined={false}
            content={<StyledTooltipP>Time selected</StyledTooltipP>}
          >
            <DateBadge
              date={getTimeDescriptionFromTimestamp(
                scheduledNextCallAt || undefined,
                scheduledNextCallAtTimeSet
              )}
            />
          </StyledTooltip>
        </StyledGridColumn>
        <StyledGridColumn>{contactName}</StyledGridColumn>
        <StyledGridColumn>{productName}</StyledGridColumn>
        <StyledGridColumn>{stageName}</StyledGridColumn>
        <StyledGridColumn>{callAttemptCount}</StyledGridColumn>
        <StyledGridColumn>
          {lastLeadEvent && (
            <StyledTooltip
              maxWidth={300}
              underlined={false}
              content={
                <>
                  <StyledTooltipP strong>
                    {formatToHuman(lastLeadEvent.type)} by{' '}
                    {lastLeadEvent?.adminUser?.name}
                  </StyledTooltipP>
                  {lastLeadEvent.metadata.notes && (
                    <StyledTooltipP>
                      {truncateText(lastLeadEvent.metadata.notes as string, 75)}
                    </StyledTooltipP>
                  )}
                </>
              }
            >
              <StyledActivityWrapper>
                <StyledIconWrapper>
                  {lastLeadEventIconType && (
                    <LastActivityIcon type={lastLeadEventIconType} />
                  )}
                </StyledIconWrapper>
                <LastActivityTime>
                  {formatLastActivityTime(lastLeadEvent.happenedAt)}
                </LastActivityTime>
              </StyledActivityWrapper>
            </StyledTooltip>
          )}
        </StyledGridColumn>
        <StyledGridColumn>
          <ConversionScoreIcon
            conversionScore={conversionScore}
            description={describeScore(row.attributes)}
          />
        </StyledGridColumn>
        <StyledGridColumn
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <OwnerAssignment
            lead={row}
            loggedInAdminUserId={loggedInAdminUserId}
            setEnabled={setEnabled}
          />
        </StyledGridColumn>
        {showBulkAssign && (
          <StyledCheckboxColumn onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onChange={() => onToggleLead(row.id)}
              aria-label={`Select lead ${row.id}`}
            />
          </StyledCheckboxColumn>
        )}
      </LeadRowLinkContents>
    </LeadDataRow>
  )
}

export const INITIAL_FILTER_VALUES: FilterValues<Filters> = {
  adminUserIds: [],
  partnerId: '',
  products: [],
  brandIds: [],
  sourceIdentifier: '',
  origin: '',
  status: '',
  unsuitableReason: '',
  blockedReason: '',
  callToday: false,
  missedLastCall: false,
  lastChance: false,
  scheduledNextCallAtTimeSet: false,
  funeralPlanGuideSent: false,
  lastActivityAt: {
    lte: '',
    gte: '',
    option: 'any',
  },
}

const INITIAL_PAGE_VALUES: PageValues<LeadAttributes> = {
  ...SORT_CONFIG['Call scheduled'],
  sortOrder: 'desc',
  size: 30,
}

const LeadList = () => {
  const [storageEnabled, setStorageEnabled] = useLocalStorage<boolean>(
    'leadListFiltersEnabled',
    false
  )

  const {
    apiSearchParams,
    filters,
    sort,
    filterHandler,
    paginateHandler,
    sortHandler,
  } = useSearchParamManager(INITIAL_FILTER_VALUES, INITIAL_PAGE_VALUES, {
    storageEnabled,
    storageKey: 'leadListFilters',
  })

  const [enabled, setEnabled] = useState(true)

  const token = useSelector((state: { token: string }) => state.token)
  const loggedInAdminUserId = getUserIdFromToken(token)

  const { data, isLoading, isError, isPreviousData } =
    useFetchArrayResource<Lead>(
      {
        resource: RESOURCES.LEADS,
        params: apiSearchParams,
      },
      {
        enabled,
        keepPreviousData: true,
      }
    )

  /** selectedLeadIds tracks the state of the leads selected for bulk assignment
   *  on the current page
   */
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([])
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)

  /** Reset the selected leads when the filters change. The filters will change the visible
   * leads, so we need to clear the selected leads */
  useEffect(() => {
    setSelectedLeadIds([])
  }, [apiSearchParams])

  const leadsOnPage = data?.data ?? []

  const allLeadsOnPageSelected =
    leadsOnPage.length > 0 &&
    leadsOnPage.every((l) => selectedLeadIds.includes(l.id))

  const someLeadsOnPageSelected =
    leadsOnPage.some((l) => selectedLeadIds.includes(l.id)) &&
    !allLeadsOnPageSelected

  useEffect(() => {
    const el = selectAllCheckboxRef.current
    if (el) el.indeterminate = someLeadsOnPageSelected
  }, [someLeadsOnPageSelected])

  const toggleLead = (leadId: number) => {
    /** Checkbox toggle behavious: if the lead is selected, remove it from the selectedLeadIds array, otherwise add it to the array */
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    )
  }

  const toggleSelectAll = () => {
    const pageIds = leadsOnPage.map((l) => l.id)
    const allSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedLeadIds.includes(id))
    if (allSelected) {
      setSelectedLeadIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...pageIds])))
    }
  }

  if (!filters || !sort) return null

  /** We only show the bulk assign feature if the user has filtered the leads list by a single owner */
  const adminUserIdsFilter = filters.adminUserIds
  const showBulkAssign =
    Array.isArray(adminUserIdsFilter) && adminUserIdsFilter.length === 1
  const isAnyLeadsSelected = selectedLeadIds.length > 0
  return (
    <Wrapper noTrim>
      <Grid
        gap="L"
        marginFromM={[0, 0, `-${GTR.XL}`]}
        marginFromL={[0, 0, `-${GTR.XXL}`]}
      >
        <Grid.Item span={3}>
          <Formik initialValues={filters} onSubmit={filterHandler}>
            <FilterPanel
              loggedInAdminUserId={loggedInAdminUserId}
              storageEnabled={storageEnabled}
              toggleStorageEnabled={() => setStorageEnabled(!storageEnabled)}
            />
          </Formik>
        </Grid.Item>
        <Grid.Item span={9} padding={['L', 0, 0]}>
          <StyledLeadsListHeader gap={0}>
            {data?.meta && (
              <P size="L" margin={[0, 0, 'M']} strong>
                {data?.meta.totalCount === 1
                  ? '1 lead'
                  : `${data?.meta.totalCount} leads`}
              </P>
            )}
            {data?.meta?.callCountSummary && (
              <StyledCallCountSummary margin={[0, 0, 'M']}>
                <StyledP size="S" strong>
                  Scheduled calls:
                </StyledP>
                <Tooltip
                  content="Leads with a scheduled call that are now in the past."
                  underlined={false}
                >
                  <StyledP size="S">
                    <StyledSpan>
                      {data?.meta?.callCountSummary.overdueCount}{' '}
                    </StyledSpan>
                    overdue
                  </StyledP>
                </Tooltip>
                <Tooltip
                  content="Leads with a call scheduled for today that are in the future."
                  underlined={false}
                >
                  <StyledP size="S">
                    <StyledSpan>
                      {data?.meta?.callCountSummary.todayCount}{' '}
                    </StyledSpan>
                    today
                  </StyledP>
                </Tooltip>
                <Tooltip
                  content="Leads with a call scheduled for tomorrow."
                  underlined={false}
                >
                  <StyledP size="S">
                    <StyledSpan>
                      {data?.meta?.callCountSummary.tomorrowCount}{' '}
                    </StyledSpan>
                    tomorrow
                  </StyledP>
                </Tooltip>
                <Tooltip
                  content="Leads with a call scheduled after tomorrow."
                  underlined={false}
                >
                  <StyledP size="S">
                    <StyledSpan>
                      {data?.meta.callCountSummary.inFutureCount}{' '}
                    </StyledSpan>
                    in the future
                  </StyledP>
                </Tooltip>
              </StyledCallCountSummary>
            )}
          </StyledLeadsListHeader>
          {showBulkAssign && (
            <StyledBulkAssignWrapper>
              <StyledLeadsSelectedSummaryWrapper>
                {/* margin on P to match Bulk Assign and Clear buttons to avoid layout shifting when buttons are toggled into view */}
                <P color={COLOR.GREY.DARK} strong margin={[11, 0]}>
                  {selectedLeadIds?.length ?? 0} lead
                  {selectedLeadIds?.length === 1 ? '' : 's'} selected
                </P>
                {isAnyLeadsSelected && (
                  <Button.Underline
                    type="button"
                    onClick={() => {
                      setSelectedLeadIds([])
                    }}
                  >
                    Clear
                  </Button.Underline>
                )}
              </StyledLeadsSelectedSummaryWrapper>
              {isAnyLeadsSelected && (
                <BulkAssignDropdown
                  selectedLeadIds={selectedLeadIds}
                  loggedInAdminUserId={loggedInAdminUserId}
                  onAssigned={() => {
                    setSelectedLeadIds([])
                    setEnabled(true)
                  }}
                />
              )}
            </StyledBulkAssignWrapper>
          )}
          <LeadGrid
            leads={data?.data || []}
            sort={sort}
            setSort={sortHandler}
            isLoading={isLoading}
            isError={isError}
            loggedInAdminUserId={loggedInAdminUserId}
            setEnabled={setEnabled}
            selectedLeadIds={selectedLeadIds}
            onToggleLead={toggleLead}
            onToggleSelectAll={toggleSelectAll}
            selectAllCheckboxRef={selectAllCheckboxRef}
            allPageSelected={allLeadsOnPageSelected}
            showBulkAssign={showBulkAssign}
          />
          <PageButtons
            disabled={isPreviousData}
            cursors={data?.meta?.cursors}
            setPage={paginateHandler}
          />
        </Grid.Item>
      </Grid>
    </Wrapper>
  )
}

export default LeadList
